// =============================================================================
// Module Project - Repository: Task Statuses (Kanban Columns/Sections)
// Seul fichier autorisé à importer supabase pour les opérations statuts
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { TaskStatus } from '../types';

function normalizeProjectStatuses(statuses: TaskStatus[], projectId: string): TaskStatus[] {
  const byCode = new Map<string, TaskStatus>();

  statuses.forEach((status) => {
    const existing = byCode.get(status.code);
    const isProjectSpecific = status.project_id === projectId;
    const existingIsProjectSpecific = existing?.project_id === projectId;

    if (!existing || (isProjectSpecific && !existingIsProjectSpecific)) {
      byCode.set(status.code, status);
    }
  });

  return Array.from(byCode.values()).sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.created_at.localeCompare(b.created_at);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH STATUSES - Project-level only (each project has its own columns)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchStatuses(orgId: string, projectId: string): Promise<TaskStatus[]> {
  const { data, error } = await supabase
    .from('project_task_statuses')
    .select('*')
    .eq('org_id', orgId)
    .eq('project_id', projectId)
    .order('position');

  if (error) throw error;

  return normalizeProjectStatuses((data || []) as TaskStatus[], projectId);
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH TERMINAL STATUS CODES (project-level only)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTerminalStatusCodes(orgId: string, projectId?: string): Promise<string[]> {
  if (!projectId) return [];

  const { data, error } = await supabase
    .from('project_task_statuses')
    .select('code, is_terminal')
    .eq('org_id', orgId)
    .eq('project_id', projectId)
    .eq('is_terminal', true);

  if (error) throw error;

  return (data || []).map((s) => s.code);
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE STATUS
// ─────────────────────────────────────────────────────────────────────────────
export async function createStatus(
  orgId: string,
  projectId: string,
  code: string,
  label: string,
  position: number
): Promise<TaskStatus> {
  const { data, error } = await supabase
    .from('project_task_statuses')
    .insert({
      org_id: orgId,
      project_id: projectId,
      code,
      label,
      position,
    })
    .select()
    .single();

  if (error) throw error;
  return data as TaskStatus;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPSERT PROJECT-SPECIFIC OVERRIDE FOR A STATUS
// ─────────────────────────────────────────────────────────────────────────────
export async function upsertProjectStatusOverride(
  orgId: string,
  projectId: string,
  sourceStatus: TaskStatus,
  updates: { label?: string; color?: string; is_terminal?: boolean; position?: number }
): Promise<TaskStatus> {
  // Check if a project-level override already exists
  const { data: existing } = await supabase
    .from('project_task_statuses')
    .select('id')
    .eq('org_id', orgId)
    .eq('project_id', projectId)
    .eq('code', sourceStatus.code)
    .maybeSingle();

  const payload = {
    org_id: orgId,
    project_id: projectId,
    code: sourceStatus.code,
    label: updates.label ?? sourceStatus.label,
    color: updates.color ?? sourceStatus.color ?? null,
    is_terminal: updates.is_terminal ?? sourceStatus.is_terminal ?? false,
    position: updates.position ?? sourceStatus.position,
  };

  if (existing) {
    const { data, error } = await supabase
      .from('project_task_statuses')
      .update(payload)
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data as TaskStatus;
  } else {
    const { data, error } = await supabase
      .from('project_task_statuses')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data as TaskStatus;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE STATUS
// ─────────────────────────────────────────────────────────────────────────────
export async function updateStatus(
  statusId: string,
  updates: { label?: string; color?: string; is_terminal?: boolean; position?: number }
): Promise<void> {
  const { error } = await supabase
    .from('project_task_statuses')
    .update(updates)
    .eq('id', statusId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE STATUS
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteStatus(statusId: string): Promise<void> {
  const { error } = await supabase
    .from('project_task_statuses')
    .delete()
    .eq('id', statusId);

  if (error) throw error;
}
